import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPaperPlane, faCheckCircle, faImage } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../AuthContext';
import Modal from '../../components/Modal';
import './AdminTickets.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';

const priorityColors = { high: 'priority-high', medium: 'priority-medium', low: 'priority-low' };
const statusColors = { pending: 'status-pending', replied: 'status-processing', closed: 'status-completed' };

export default function AdminTickets() {
  const { token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [modal, setModal] = useState({ open: false, type: 'info', title: '', message: '' });
  const replyFileRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchTickets = async () => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (search) params.set('search', search);
    const res = await fetch(`${API}/admin/tickets?${params}`, { headers: jsonHeaders });
    const data = await res.json();
    setTickets(Array.isArray(data) ? data : []);
  };

  useEffect(() => { fetchTickets(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!search) return;
    const timer = setTimeout(fetchTickets, 300);
    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectTicket = (t) => setSelected(t);

  const buildFormData = (data, file) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append('attachment', file);
    return fd;
  };

  const handleReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    const res = await fetch(`${API}/admin/tickets/${selected.id}/reply`, {
      method: 'POST',
      headers: authHeaders,
      body: buildFormData({ message: reply }, replyFile),
    });
    const data = await res.json();
    setSending(false);
    if (!res.ok) { setModal({ open: true, type: 'error', title: 'Reply Failed', message: data.message || 'Could not send reply.' }); return; }
    setReply('');
    setReplyFile(null);
    if (replyFileRef.current) replyFileRef.current.value = '';
    fetchTickets();
    const updated = await (await fetch(`${API}/tickets/${selected.id}`, { headers: authHeaders })).json();
    if (updated.id) setSelected(updated);
  };

  const closeTicket = async (id) => {
    await fetch(`${API}/admin/tickets/${id}`, {
      method: 'PUT', headers: jsonHeaders,
      body: JSON.stringify({ status: 'closed' }),
    });
    fetchTickets();
    if (selected?.id === id) setSelected({ ...selected, status: 'closed' });
  };

  const reopenTicket = async (id) => {
    await fetch(`${API}/admin/tickets/${id}`, {
      method: 'PUT', headers: jsonHeaders,
      body: JSON.stringify({ status: 'pending' }),
    });
    fetchTickets();
    if (selected?.id === id) setSelected({ ...selected, status: 'pending' });
  };

  const AttachmentDisplay = ({ reply: r }) => {
    if (!r.attachment) return null;
    const url = `${API.replace('/api', '')}/storage/${r.attachment}`;
    return (
      <div className="msg-attachment">
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img src={url} alt="Attachment" className="msg-attachment-img" />
        </a>
      </div>
    );
  };

  const filteredSearch = tickets.filter((t) =>
    !search || t.subject.toLowerCase().includes(search.toLowerCase()) ||
    t.user?.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="admin-tickets">
      <div className="tickets-layout">
        <div className="tickets-list-panel">
          <div className="orders-filters">
            <div className="search-bar">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input type="text" className="search-input" placeholder="Search tickets..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="replied">Replied</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div className="tickets-cards">
            {filteredSearch.map((t) => (
              <div
                key={t.id}
                className={`ticket-row ${selected?.id === t.id ? 'selected' : ''}`}
                onClick={() => selectTicket(t)}
              >
                <div className="ticket-row-top">
                  <span className="ticket-row-id">#{t.id}</span>
                  <span className={`status-badge ${statusColors[t.status]}`}>{t.status}</span>
                </div>
                <div className="ticket-row-subject">{t.subject}</div>
                <div className="ticket-row-meta">
                  <span className="ticket-row-user">{t.user?.name || 'Unknown'}</span>
                  <span className={`priority-badge ${priorityColors[t.priority]}`}>{t.priority}</span>
                  <span className="ticket-row-date">{new Date(t.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
            {filteredSearch.length === 0 && <p className="tickets-empty">No tickets found.</p>}
          </div>
        </div>

        <div className="ticket-detail-panel">
          {selected ? (
            <>
              <div className="ticket-detail-header">
                <div>
                  <h2 className="ticket-detail-subject">{selected.subject}</h2>
                  <span className="ticket-detail-meta">
                    #{selected.id} — {selected.user?.name || 'Unknown'} — {new Date(selected.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="ticket-detail-actions">
                  {selected.status !== 'closed' ? (
                    <button className="close-ticket-btn" onClick={() => closeTicket(selected.id)}>
                      <FontAwesomeIcon icon={faCheckCircle} /> Close Ticket
                    </button>
                  ) : (
                    <button className="reopen-ticket-btn" onClick={() => reopenTicket(selected.id)}>Reopen</button>
                  )}
                </div>
              </div>

              <div className="ticket-messages">
                <div className="ticket-msg user-msg">
                  <div className="msg-header">
                    <span className="msg-author">{selected.user?.name || 'User'}</span>
                    <span className="msg-date">{new Date(selected.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="msg-body">{selected.message}</p>
                </div>
                {(selected.replies || []).map((r) => (
                  <div key={r.id} className={`ticket-msg ${r.user?.role === 'admin' ? 'admin-msg' : 'user-msg'}`}>
                    <div className="msg-header">
                      <span className="msg-author">{r.user?.role === 'admin' ? 'Support' : r.user?.name || 'User'}</span>
                      <span className="msg-date">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="msg-body">{r.message}</p>
                    <AttachmentDisplay reply={r} />
                  </div>
                ))}
              </div>

              {selected.status !== 'closed' && (
                <div className="ticket-reply-box">
                  <textarea className="form-input reply-textarea" placeholder="Type your reply..." rows={3} value={reply} onChange={(e) => setReply(e.target.value)} />
                  <div className="reply-attach-row">
                    <label className="reply-attach-label">
                      <FontAwesomeIcon icon={faImage} />
                      <input type="file" ref={replyFileRef} accept="image/*" className="file-input" onChange={(e) => setReplyFile(e.target.files[0])} />
                    </label>
                    {replyFile && <span className="reply-file-name">{replyFile.name}</span>}
                    <button className="reply-btn" onClick={handleReply} disabled={sending}>
                      <FontAwesomeIcon icon={faPaperPlane} />
                      <span>{sending ? 'Sending...' : 'Send Reply'}</span>
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="ticket-empty">
              <p>Select a ticket to view details</p>
            </div>
          )}
        </div>
      </div>

      <Modal open={modal.open} type={modal.type} title={modal.title} message={modal.message} onClose={() => setModal({ ...modal, open: false })} />
    </div>
  );
}
